import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import SuggestionChips from './SuggestionChips';
import { 
  parseResponse, 
  detectResponseType, 
  getResponseTypeClass,
  shouldFormatAsCurrency,
  shouldFormatAsPercentage,
  shouldFormatAsSalaryDifference,
  formatCurrency,
  formatPercentage,
  formatSOCCode,
  parseJobPostings,
  parseBusinessListings,
  parseWageComparison,
  parseCareerPathSteps,
  extractTipText
} from '../utils/chatbotResponseParser';

interface SuggestedPrompt {
  text: string;
  query: string;
  icon: string;
}

interface ChatResponseProps {
  response: string;
  suggestedPrompts?: SuggestedPrompt[] | null;
  onSelectSuggestion?: (query: string) => void;
}

const ChatResponse: React.FC<ChatResponseProps> = ({ 
  response, 
  suggestedPrompts = null,
  onSelectSuggestion 
}) => {
  // Memoize the parsing to prevent duplicate calls
  const { responseType, parsedResponse } = useMemo(() => {
    const type = detectResponseType(response);
    const parsed = parseResponse(response);
    return { responseType: type, parsedResponse: parsed };
  }, [response]);
  
  // Parse special response types
  const jobPostings = useMemo(() => parseJobPostings(response), [response]);
  const businessListings = useMemo(() => parseBusinessListings(response), [response]);
  const wageComparison = useMemo(() => parseWageComparison(response), [response]);
  const careerSteps = useMemo(() => parseCareerPathSteps(response), [response]);
  const tipText = useMemo(() => extractTipText(response), [response]);

  // Render Job Postings
  if (jobPostings && jobPostings.jobs.length > 0) {
    return (
      <div className="chatbot-job-postings-response">
        <div className="chatbot-response-header">
          <h3 className="emoji-title">💼 {jobPostings.title}</h3>
        </div>
        <div className="job-postings-grid">
          {jobPostings.jobs.map((job, index) => (
            <div key={index} className="job-posting-item">
              <div className="job-posting-number">{index + 1}</div>
              <div className="job-posting-details">
                <h4 className="job-posting-title">{job.title}</h4>
                <div className="job-posting-meta">
                  <span className="job-company">🏢 {job.company}</span>
                  <span className="job-location">📍 {job.location}</span>
                </div>
                {job.applyUrl && (
                  <a 
                    href={job.applyUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="job-apply-button"
                  >
                    Apply Now →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        {tipText && (
          <div className="chatbot-tip-box">
            <span className="tip-icon">💡</span>
            <p>{tipText}</p>
          </div>
        )}
        {/* Suggestion Chips */}
        {suggestedPrompts && onSelectSuggestion && (
          <SuggestionChips 
            suggestions={suggestedPrompts} 
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    );
  }

  // Render Business Listings
  if (businessListings && businessListings.businesses.length > 0) {
    return (
      <div className="chatbot-business-listings-response">
        <div className="chatbot-response-header">
          <h3 className="emoji-title">🏢 {businessListings.title}</h3>
        </div>
        <div className="business-listings-grid">
          {businessListings.businesses.map((business, index) => (
            <div key={index} className="business-listing-item">
              <div className="business-number">{index + 1}</div>
              <div className="business-details">
                <h4 className="business-name">{business.name}</h4>
                <p className="business-location">{business.location}</p>
                {business.industry && (
                  <p className="business-industry">
                    <span className="industry-badge">{business.industry}</span>
                  </p>
                )}
                <div className="business-actions">
                  {business.phone && (
                    <a href={`tel:${business.phone}`} className="business-phone">
                      📞 {business.phone}
                    </a>
                  )}
                  {business.mapUrl && (
                    <a 
                      href={business.mapUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="business-map-button"
                    >
                      📍 View on Map
                    </a>
                  )}
                </div>
                {business.naics && (
                  <span className="naics-badge">NAICS {business.naics}</span>
                )}
              </div>
            </div>
          ))}
        </div>
        {tipText && (
          <div className="chatbot-tip-box">
            <span className="tip-icon">💡</span>
            <p>{tipText}</p>
          </div>
        )}
        {/* Suggestion Chips */}
        {suggestedPrompts && onSelectSuggestion && (
          <SuggestionChips 
            suggestions={suggestedPrompts} 
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    );
  }

  // Render Wage Comparison
  if (wageComparison) {
    return (
      <div className="chatbot-wage-comparison-response">
        <div className="chatbot-response-header">
          <h3 className="emoji-title">💰 {wageComparison.title}</h3>
          {wageComparison.subtitle && (
            <p className="response-subtitle">{wageComparison.subtitle}</p>
          )}
        </div>
        <div className="wage-comparison-container">
          <div className="occupation-info">
            <h4 className="occupation-name">{wageComparison.occupation}</h4>
            {wageComparison.socCode && (
              <span className="soc-badge">SOC {wageComparison.socCode}</span>
            )}
          </div>
          <div className="wage-columns">
            <div className="wage-card">
              <div className="wage-label">Sumner County</div>
              <div className="wage-amount">${wageComparison.sumnerWage?.toLocaleString()}</div>
            </div>
            <div className="wage-card">
              <div className="wage-label">Tennessee</div>
              <div className="wage-amount">${wageComparison.stateWage?.toLocaleString()}</div>
            </div>
            <div className="wage-card wage-card-highlight">
              <div className="wage-label">National Average</div>
              <div className="wage-amount">${wageComparison.nationalWage?.toLocaleString()}</div>
            </div>
          </div>
          {wageComparison.insights && wageComparison.insights.length > 0 && (
            <div className="wage-insights">
              {wageComparison.insights.map((insight, index) => (
                <div key={index} className="wage-insight-item">
                  {insight.startsWith('✅') ? (
                    <span className="insight-positive">{insight}</span>
                  ) : (
                    <span>{insight}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {wageComparison.note && (
            <div className="wage-note">
              <em>{wageComparison.note}</em>
            </div>
          )}
        </div>
        {/* Suggestion Chips */}
        {suggestedPrompts && onSelectSuggestion && (
          <SuggestionChips 
            suggestions={suggestedPrompts} 
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    );
  }

  // Render Career Path Steps
  if (careerSteps && careerSteps.length > 0) {
    return (
      <div className="chatbot-career-path-response">
        <div className="chatbot-response-header">
          <h3 className="emoji-title">🎓 Career Pathway</h3>
        </div>
        <div className="career-steps-timeline">
          {careerSteps.map((step, index) => (
            <div key={index} className="career-step-item">
              <div className="step-number">{step.stepNumber}</div>
              <div className="step-content">
                <h4 className="step-title">{step.title}</h4>
                <div className="step-details">
                  {step.education && (
                    <div className="step-detail">
                      <strong>Education:</strong> {step.education}
                    </div>
                  )}
                  {step.requirements && (
                    <div className="step-detail">
                      <strong>Requirements:</strong> {step.requirements}
                    </div>
                  )}
                  {step.salary && (
                    <div className="step-detail">
                      <strong>Salary:</strong> {step.salary}
                    </div>
                  )}
                  {step.time && (
                    <div className="step-detail">
                      <strong>Time:</strong> {step.time}
                    </div>
                  )}
                </div>
              </div>
              {index < careerSteps.length - 1 && (
                <div className="step-connector"></div>
              )}
            </div>
          ))}
        </div>
        {tipText && (
          <div className="chatbot-tip-box">
            <span className="tip-icon">💡</span>
            <p>{tipText}</p>
          </div>
        )}
        {/* Suggestion Chips */}
        {suggestedPrompts && onSelectSuggestion && (
          <SuggestionChips 
            suggestions={suggestedPrompts} 
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    );
  }
  
  // If it's a default response (no table), render as markdown with enhanced formatting
  if (responseType === 'default') {
    return (
      <div className="chatbot-default-response">
        <ReactMarkdown 
          components={{
            // Custom rendering for links
            a: ({ node, ...props }) => (
              <a 
                {...props} 
                target="_blank" 
                rel="noopener noreferrer"
                className="chatbot-link"
              />
            ),
            // Custom rendering for paragraphs starting with emojis
            p: ({ node, children, ...props }) => {
              const text = String(children);
              if (text.startsWith('💡')) {
                return (
                  <div className="chatbot-tip-box" {...props}>
                    <span className="tip-icon">💡</span>
                    <p>{text.replace(/^💡\s*/, '')}</p>
                  </div>
                );
              }
              return <p {...props}>{children}</p>;
            },
          }}
        >
          {response}
        </ReactMarkdown>
        {/* Suggestion Chips */}
        {suggestedPrompts && onSelectSuggestion && (
          <SuggestionChips 
            suggestions={suggestedPrompts} 
            onSelectSuggestion={onSelectSuggestion}
          />
        )}
      </div>
    );
  }
  
  const responseClass = getResponseTypeClass(responseType);
  
  return (
    <div className={responseClass}>
      {/* Table Header */}
      {parsedResponse.title && (
        <div className="chatbot-table-header">
          <h4 className="chatbot-table-title">{parsedResponse.title}</h4>
        </div>
      )}
      
      {/* Table Content */}
      {parsedResponse.table && (
        <div className="chatbot-table-container">
          <table className="chatbot-response-table">
            <thead>
              <tr>
                {parsedResponse.table.headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsedResponse.table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {parsedResponse.table!.headers.map((header, colIndex) => {
                    const value = row[header];
                    let formattedValue = value;
                    let cellClass = '';
                    
                    // Format currency values
                    if (shouldFormatAsCurrency(header, value)) {
                      formattedValue = formatCurrency(value);
                      cellClass = 'chatbot-currency';
                    }
                    // Format percentage values
                    else if (shouldFormatAsPercentage(header, value)) {
                      formattedValue = formatPercentage(value);
                      cellClass = 'chatbot-percentage';
                    }
                    // Format salary difference (special handling)
                    else if (shouldFormatAsSalaryDifference(header, value)) {
                      // Extract the amount from "X Higher" format
                      const match = value.match(/\$?([\d,]+)\s+Higher/i);
                      if (match) {
                        formattedValue = formatCurrency(match[1]) + ' Higher';
                        cellClass = 'chatbot-salary-difference';
                      } else {
                        formattedValue = value;
                      }
                    }
                    // Format SOC codes
                    else if (header.toLowerCase().includes('soc')) {
                      formattedValue = formatSOCCode(value);
                      cellClass = 'chatbot-soc-code';
                    }
                    // Format rank values
                    else if (header.toLowerCase().includes('rank')) {
                      cellClass = 'chatbot-rank';
                    }
                    
                    return (
                      <td key={colIndex} className={cellClass}>
                        {formattedValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Insights Section */}
      {parsedResponse.insights && (
        <div className="chatbot-insights-section">
          <div className="chatbot-insights-title">Key Insights</div>
          <div className="chatbot-insights-content">
            <ReactMarkdown>{parsedResponse.insights}</ReactMarkdown>
          </div>
        </div>
      )}
      
      {/* Recommendations Section */}
      {parsedResponse.recommendations && (
        <div className="chatbot-recommendations-section">
          <div className="chatbot-recommendations-title">Recommendation</div>
          <div className="chatbot-recommendations-content">
            <ReactMarkdown>{parsedResponse.recommendations}</ReactMarkdown>
          </div>
        </div>
      )}
      
      {/* Tip Text */}
      {tipText && (
        <div className="chatbot-tip-box">
          <span className="tip-icon">💡</span>
          <p>{tipText}</p>
        </div>
      )}
      
      {/* Suggestion Chips */}
      {suggestedPrompts && onSelectSuggestion && (
        <SuggestionChips 
          suggestions={suggestedPrompts} 
          onSelectSuggestion={onSelectSuggestion}
        />
      )}
    </div>
  );
};

export default ChatResponse;
